import type {
    DocumentType,
    IR2ThreadCreateIfNotExistsResponse,
    IR2ThreadDocumentMeta,
    IR2ThreadIdAssignResponse,
} from '@common/types/api/r2-types';
import { R2ApiResult } from '@common/types/api/r2-types';
import { ISessionInfo } from '@core/base/session-base.types';
import { OfficeService } from '@core/office/office-service/office-service';
import { OfficeSessionService } from '@core/office/office-session/office.session.service';
import { OutlookHost } from '@core/shared/office-hosts/outlook-host';
import type { IOfficeHost } from '@core/shared/office-hosts/office-host.types';
import { SessionCryptoService } from '@services/session-crypto/session-crypto.service';
import { resolveMessageScopedDocumentId } from '@core/shared/office-document-id';

const VENDOR_ID = 'microsoft' as const;

export interface OfficeDocumentThreadContext
{
    key: string;
    meta: IR2ThreadDocumentMeta;
    threadId: string;
}

export function documentTypeFromOfficeHostTag(tag: string): DocumentType
{
    switch (tag)
    {
        case 'word':
            return 'word';
        case 'excel':
            return 'excel';
        case 'outlook':
            return 'mail';
        case 'powerpoint':
            return 'power_point';
        case 'onenote':
            return 'note';
        default:
            throw new Error(`Unsupported Office host tag: ${ tag }`);
    }
}

export function buildOfficeThreadKey(meta: IR2ThreadDocumentMeta): string
{
    return `${ meta.vendor_id }_${ meta.type }_${ meta.id }_${ meta.user_id }`;
}

/**
 * The document's main AI conversation — the one the AI tab shows. Threads other
 * than this one are addressed by appending a scope to the document id (see
 * {@link OfficeDocumentThreadManager.ensureThread}).
 */
export const PRIMARY_THREAD_SCOPE = '';

export class OfficeDocumentThreadManager
{
    // scope -> resolved context. The document can carry several threads (the AI
    // tab's conversation, the group chat's summaries, ...), each with its own
    // key and thread id; the scope is what tells them apart.
    private contexts = new Map<string, OfficeDocumentThreadContext>();

    constructor(
        private sessionService: OfficeSessionService,
        private officeService: OfficeService,
        private sessionCrypto: SessionCryptoService,
    ) { }

    public getContext(scope: string = PRIMARY_THREAD_SCOPE): OfficeDocumentThreadContext | null
    {
        return this.contexts.get(scope) ?? null;
    }

    /** Drops every resolved thread; the next ensureThread re-resolves. */
    public reset(): void
    {
        this.contexts.clear();
    }

    public async ensureThread(forceRefresh = false, scope: string = PRIMARY_THREAD_SCOPE): Promise<OfficeDocumentThreadContext>
    {
        const cached = this.contexts.get(scope);
        if (cached && !forceRefresh)
        {
            return cached;
        }

        const context = await this.resolveContext(scope);
        const response = await this.sessionCrypto.postAddin<unknown, IR2ThreadCreateIfNotExistsResponse>(
            '/thread/create-if-not-exists',
            {
                key: context.key,
                meta: context.meta,
            },
        );

        if (response.result !== R2ApiResult.Success || !response.thread_id)
        {
            throw new Error('Thread create-if-not-exists failed');
        }

        const resolved: OfficeDocumentThreadContext = {
            ...context,
            threadId: response.thread_id,
        };
        this.contexts.set(scope, resolved);

        return resolved;
    }

    public async assignThreadId(oldThreadId: string): Promise<void>
    {
        const context = this.contexts.get(PRIMARY_THREAD_SCOPE) ?? await this.resolveContext(PRIMARY_THREAD_SCOPE);

        const response = await this.sessionCrypto.postAddin<unknown, IR2ThreadIdAssignResponse>(
            '/thread/id/assign',
            {
                thread_id: oldThreadId,
                key: context.key,
                meta: context.meta,
            },
        );

        if (response.result !== R2ApiResult.Success)
        {
            throw new Error('Thread id assign failed');
        }

        this.contexts.set(PRIMARY_THREAD_SCOPE, {
            ...context,
            threadId: response.thread_id ?? oldThreadId,
        });
    }

    // A scope other than the primary one is appended to the document id, so the
    // thread key stays the same shape (`vendor_type_id_userId`) and the extra
    // thread still belongs to this user and this document. Those threads also
    // take the narrower document identity: consolidating a mail conversation
    // into one thread is a choice the AI tab makes for itself, and a companion
    // thread (e.g. the group chat's summaries) must not span two documents its
    // own feature keeps apart.
    private async resolveContext(scope: string): Promise<OfficeDocumentThreadContext>
    {
        const session = this.sessionService.getSession();
        const userId = this.requireUserId(session);
        const host = this.officeService.getHost();
        const id = scope === PRIMARY_THREAD_SCOPE
            ? await this.resolveDocumentId(host)
            : await resolveMessageScopedDocumentId(host);
        const type = documentTypeFromOfficeHostTag(host.getTag());
        const meta: IR2ThreadDocumentMeta = {
            vendor_id: VENDOR_ID,
            type,
            id: `${ id }${ scope }`,
            user_id: userId,
        };

        return {
            key: buildOfficeThreadKey(meta),
            meta,
            threadId: '',
        };
    }

    private requireUserId(session: ISessionInfo): string
    {
        if (!session.user_id)
        {
            throw new Error('Missing user id for thread key');
        }

        return session.user_id;
    }

    // getFirstMessageIdByConversationAsync is temporarily disabled - it resolves to
    // a message id scoped to the calling user's own mailbox, so the same email ends
    // up with a different document id per recipient. conversationId is computed by
    // Exchange from the thread itself and is shared across mailboxes, so it's used
    // directly instead. Unlike group chat, this keeps conversationId (not
    // internetMessageId) as the primary id so a reply thread still consolidates
    // into one AI chat document.
    private async resolveDocumentId(host: IOfficeHost): Promise<string>
    {
        if (host instanceof OutlookHost)
        {
            const conversationId = await host.getConversationIdAsync();
            if (conversationId)
            {
                return conversationId;
            }
        }

        return host.getDocumentIDAsync();
    }
}
